/**
 * SessionViewManager — persistent global layout route that wraps every page.
 *
 * It is the element of a top-level <Route element={<SessionViewManager />}> in App.tsx,
 * so React Router keeps it mounted for ANY URL in the app. Active page content renders
 * via <Outlet />; background chat sessions survive navigation to /analyst, /workflows, etc.
 *
 * WHAT IT OWNS
 *   • analystsList / isLoadingAnalysts  — loaded once, shared as props to all ChatPages
 *   • sessionIds: string[]              — registry of all sessions visited in this tab
 *   • chatAnalystId URL redirect        — pre-navigation handling from AnalystPage
 *
 * HOW CONCURRENT STREAMING WORKS
 * Each session in `sessionIds` maps to one ChatPage instance via React's `key` prop.
 * React guarantees that a unique `key` produces a unique component fiber — each
 * ChatPage gets its own independent useState, useChatStreaming, useArtifactStore, etc.
 * This is the React equivalent of Map<sessionId, hookInstance>.
 *
 * When the user navigates from C1 (streaming) → any other page:
 *   • C1's ChatPage receives isActive=false → returns null (UI hidden)
 *   • C1 stays MOUNTED — React still runs all its hooks
 *   • C1's useChatStreaming keeps writing tokens to C1's own chatMessages
 *   • The active page renders via <Outlet />, completely unaffected
 *
 * Navigating back to C1 flips isActive=true → ChatPage renders with the
 * fully accumulated (or still-streaming) state. No data is lost.
 */
import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";
import showToast from "hooks/useCustomToast";
import { useURLContext } from "context/URLContext";
import { HeaderWithStats } from "services/MetadataServices";
import { fetchAnalystList } from "services/serviceUtils";
import { DarwinHeader } from "build-ts/common";
import { ScheduledJobProto } from "build-ts/schedule";
import ChatPage from "./ChatPage";

interface SessionViewManagerProps {
  systemPrompt?: string;
  isFromEva?: boolean;
  initialChatSessionHeader?: DarwinHeader;
  onEvaSessionsRefresh?: () => void;
  initialAnalystId?: string;
  initialPrompt?: string;
}

const SessionViewManager: React.FC<SessionViewManagerProps> = ({
  systemPrompt = "",
  isFromEva = false,
  initialChatSessionHeader,
  onEvaSessionsRefresh,
  initialAnalystId: initialAnalystIdProp,
  initialPrompt: initialPromptProp,
}) => {
  const location = useLocation();
  // useMatch reads the active child route's :id param. useParams() won't work here because
  // SessionViewManager is the layout route parent — child params aren't visible via useParams().
  const chatRouteMatch = useMatch("/chat/:id");
  const chatSessionId = chatRouteMatch?.params.id; // "new" | "abc123" | undefined
  const navigate = useNavigate();
  const { chatAnalystId } = useURLContext();

  // Navigation state set by NewChatPage for the new-session flow.
  const navState = location.state as {
    initialPrompt?: string;
    analystId?: string;
    isNewSession?: boolean;
    sessionId?: string;
    scenarioJobs?: ScheduledJobProto[];
  } | null;

  const initialPrompt = initialPromptProp || navState?.initialPrompt;
  const initialAnalystId = navState?.analystId || initialAnalystIdProp;

  // ── Shared global state ──────────────────────────────────────────────────────
  const [analystsList, setAnalystsList] = useState<HeaderWithStats[]>([]);
  const [isLoadingAnalysts, setIsLoadingAnalysts] = useState(true);

  // ── Session registry ─────────────────────────────────────────────────────────
  // One entry per visited session. Each entry renders one ChatPage instance keyed
  // by sessionId, giving it a fully isolated React fiber.
  const [sessionIds, setSessionIds] = useState<string[]>([]);

  // ── Load analyst list once (shared across all ChatPage instances) ────────────
  useEffect(() => {
    const load = async () => {
      setIsLoadingAnalysts(true);
      const list = await fetchAnalystList(false);
      setAnalystsList(list);
      setIsLoadingAnalysts(false);
    };
    load();
  }, []);

  // ── Register a new ChatPage instance when the active session changes ─────────
  useEffect(() => {
    if (!chatSessionId || chatSessionId === "new") return;
    setSessionIds(prev => (prev.includes(chatSessionId) ? prev : [...prev, chatSessionId]));
  }, [chatSessionId]);

  // ── Handle chatAnalystId URL param (AnalystPage → Chat button) ───────────────
  // Resolves the analyst and redirects to /chat/new before any ChatPage mounts.
  useEffect(() => {
    const urlHasChatAnalystId = location.search.includes("chatAnalystId");
    if (!urlHasChatAnalystId || !chatAnalystId || isFromEva) return;
    if (isLoadingAnalysts || analystsList.length === 0) return;

    const analyst = analystsList.find(a => a.id === chatAnalystId);
    if (analyst) {
      const prevInitialPrompt = (location.state as { initialPrompt?: string } | null)?.initialPrompt;
      navigate("/chat/new", { replace: true, state: { preserveAnalyst: true, initialPrompt: prevInitialPrompt } });
    } else {
      showToast("Analyst not found", "", "error");
      navigate("/chat/new", { replace: true });
    }
  }, [chatAnalystId, analystsList, isLoadingAnalysts, isFromEva]);

  return (
    <>
      {/* Outlet renders the active page for every route in the app:
          - /chat/new        → <NewChatPage />
          - /chat/:id        → null  (chat UI rendered by the active ChatPage instance below)
          - /analyst, etc.   → the corresponding page component
          SessionViewManager stays mounted for ALL navigation, so background streams
          are never interrupted regardless of which page the user navigates to. */}
      <Outlet />

      {sessionIds.map(id => (
        <ChatPage
          key={id}
          sessionId={id}
          isActive={id === chatSessionId}
          analystsList={analystsList}
          isLoadingAnalysts={isLoadingAnalysts}
          systemPrompt={systemPrompt}
          isFromEva={isFromEva}
          initialChatSessionHeader={initialChatSessionHeader}
          onEvaSessionsRefresh={onEvaSessionsRefresh}
          initialPrompt={initialPrompt}
          initialAnalystId={initialAnalystId}
        />
      ))}
    </>
  );
};

export default SessionViewManager;
